import { FC, memo, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AuthUtils, ILoggedUser } from 'Utils/AuthUtils';
import { IRenderingSignalR } from 'Data/interfaces/ListagemArquivos/IListFilesResponse';
import { ViewerActions } from 'Store/Viewer/Viewer.actions';
import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { FederatedViewerActions } from 'Store/FederatedViewer/FederatedViewer.actions';
import { PanelsActions } from 'Store/Panels/Panels.actions';
import { setFileStatusAutodeskRendering } from 'Hooks/files/useQueryVerifyAutodeskProfressFiles';
import { queryClient } from 'Context/QueryClientContext';
import { FILES_QUERY_KEY } from 'Data/keys/QueryKeys';

export interface ISignalRResponse {
  api?: string;
  fileIdentifier: string;
  progress: string;
  urn?: string;
  version?: undefined;
  timeStamp?: number;
}

interface IViewerSignalRProps {
  constructionSiteId?: number;
  folderId?: string;
}

const ViewerSignalR: FC<IViewerSignalRProps> = ({
  constructionSiteId,
  folderId,
}) => {
  const dispatch = useDispatch();

  const [connection, setConnection] = useState<HubConnection>();
  const user: ILoggedUser = AuthUtils.getLoggedUser();

  useEffect(() => {
    const newConnection = new HubConnectionBuilder()
      .withUrl(
        `${process.env.REACT_APP_BASE_API_URL?.toString().replace('/api', '')}/hubs/autodesk?userId=${user.userId}`,
      )
      .configureLogging(LogLevel.Information)
      .withAutomaticReconnect()
      .build();

    newConnection.serverTimeoutInMilliseconds = 100000;
    setConnection(newConnection);
  }, []);

  useEffect(() => {
    if (!connection) { return; }

    connection
      .start()
      .then((result) => {
        connection.on('onDownloadChange', (message: string) => {
          const response = JSON.parse(message);

          const data: IRenderingSignalR = {
            etapa: 1,
            fileIdentifier: response.fileIdentifier,
            progress: Number(response.progress),
          };
          if (constructionSiteId) {
            if (folderId) {
              setFileStatusAutodeskRendering(constructionSiteId, folderId, data);
            } else {
              queryClient.invalidateQueries({
                queryKey: [FILES_QUERY_KEY, Number(constructionSiteId)],
              });
            }
          }
          dispatch(ViewerActions.updateStatusFileViewerAutodesk(data));
          dispatch(FederatedViewerActions.autodeskViewerRenderingResponse(data));
          dispatch(FederatedViewerActions.updateStatusFileViewerAutodesk(data));
          dispatch(PanelsActions.panelsAutodeskViewerRenderingResponse(data));
          dispatch(PanelsActions.updateStatusFilePanelViewer(data));
        });

        connection.on('onUploadChange', (message: string) => {
          const response = JSON.parse(message);

          const data: IRenderingSignalR = {
            etapa: 2,
            fileIdentifier: response.fileIdentifier,
            progress: Number(response.progress),
          };
          if (constructionSiteId) {
            if (folderId) {
              setFileStatusAutodeskRendering(constructionSiteId, folderId, data);
            } else {
              queryClient.invalidateQueries({
                queryKey: [FILES_QUERY_KEY, Number(constructionSiteId)],
              });
            }
          }
          dispatch(ViewerActions.updateStatusFileViewerAutodesk(data));
          dispatch(FederatedViewerActions.autodeskViewerRenderingResponse(data));
          dispatch(FederatedViewerActions.updateStatusFileViewerAutodesk(data));
          dispatch(PanelsActions.panelsAutodeskViewerRenderingResponse(data));
          dispatch(PanelsActions.updateStatusFilePanelViewer(data));
        });

        connection.on('onProgressChange', (message: string) => {
          const response = JSON.parse(message);

          const data: IRenderingSignalR = {
            etapa: 3,
            fileIdentifier: response.fileIdentifier,
            progress: Number(response.progress.split('%')[0]),
          };
          if (constructionSiteId) {
            if (folderId) {
              setFileStatusAutodeskRendering(constructionSiteId, folderId, data);
            } else {
              queryClient.invalidateQueries({
                queryKey: [FILES_QUERY_KEY, Number(constructionSiteId)],
              });
            }
          }
          dispatch(ViewerActions.updateStatusFileViewerAutodesk(data));
          dispatch(FederatedViewerActions.autodeskViewerRenderingResponse(data));
          dispatch(FederatedViewerActions.updateStatusFileViewerAutodesk(data));
          dispatch(PanelsActions.panelsAutodeskViewerRenderingResponse(data));
          dispatch(PanelsActions.updateStatusFilePanelViewer(data));
        });

        connection.on('itemRendered', (message: string) => {
          const response = JSON.parse(message);

          const data: IRenderingSignalR = {
            etapa: 4,
            fileIdentifier: response.fileIdentifier,
            progress: 100,
            urn: response.urn,
            bucketFileName: response.bucketFileName,
          };
          if (constructionSiteId) {
            if (folderId) {
              setFileStatusAutodeskRendering(constructionSiteId, folderId, data);
            } else {
              queryClient.invalidateQueries({
                queryKey: [FILES_QUERY_KEY, Number(constructionSiteId)],
              });
            }
          }
          dispatch(ViewerActions.updateStatusFileViewerAutodesk(data));
          dispatch(FederatedViewerActions.autodeskViewerRenderingResponse(data));
          dispatch(FederatedViewerActions.updateStatusFileViewerAutodesk(data));
          dispatch(PanelsActions.panelsAutodeskViewerRenderingResponse(data));
          dispatch(PanelsActions.updateStatusFilePanelViewer(data));
        });

        connection.on('itemError', (message: string) => {
          const response = JSON.parse(message);

          const data: IRenderingSignalR = {
            etapa: 5,
            fileIdentifier: response.fileIdentifier,
            progress: 0,
            urn: response.urn,
          };
          if (constructionSiteId) {
            if (folderId) {
              setFileStatusAutodeskRendering(constructionSiteId, folderId, data);
            } else {
              queryClient.invalidateQueries({
                queryKey: [FILES_QUERY_KEY, Number(constructionSiteId)],
              });
            }
          }
          dispatch(ViewerActions.updateStatusFileViewerAutodesk(data));
          dispatch(FederatedViewerActions.autodeskViewerRenderingResponse(data));
          dispatch(FederatedViewerActions.updateStatusFileViewerAutodesk(data));
          dispatch(PanelsActions.panelsAutodeskViewerRenderingResponse(data));
          dispatch(PanelsActions.updateStatusFilePanelViewer(data));
        });
      })
      .catch((e) => {
        // console.log('Connection failed: ', e)
      });

    return () => {
      connection.stop();
    };
  }, [connection, constructionSiteId, folderId]);

  return <></>;
};

export default memo(ViewerSignalR, () => true);
